import { TranslationKey } from './de';

export const en: Record<TranslationKey, string> = {
  // Header
  'header.title': 'Go-Sovereign Cloud Connector',
  'header.regulatory': 'Regulatory Overview',
  
  // Intro
  'intro.title': 'Welcome to Go-Sovereign Cloud Connector',
  'intro.subtitle': 'Find the right cloud provider for your requirements',
  'intro.description': 'This interactive questionnaire helps you evaluate and compare cloud providers based on your specific requirements.',
  'intro.start': 'Start Questionnaire',
  'intro.features.title': 'Features',
  'intro.features.questionnaire': 'Interactive Questionnaire',
  'intro.features.comparison': 'Provider Comparison',
  'intro.features.visualization': 'Visualizations',
  'intro.features.export': 'PDF Export',
  
  // Questionnaire
  'questionnaire.close': 'Close',
  'questionnaire.back': 'Back',
  'questionnaire.next': 'Next',
  'questionnaire.skip': 'Skip',
  'questionnaire.progress': 'Question {current} of {total}',
  'questionnaire.category': 'Category',
  
  // Provider Selection
  'providers.title': 'Which providers would you like to compare?',
  'providers.subtitle': 'Select at least one cloud provider',
  'providers.select': 'Select providers',
  'providers.proceed': 'Continue',
  'providers.selectAtLeastOne': 'Please select at least one provider',
  
  // IT Landscape
  'itLandscape.title': 'What does your current IT landscape look like?',
  'itLandscape.subtitle': 'Please select an option',
  'itLandscape.onprem': 'On-Premise',
  'itLandscape.private': 'Private Cloud',
  'itLandscape.hybrid': 'Hybrid Cloud',
  'itLandscape.public': 'Public Cloud',
  
  // Cloud Usage
  'cloudUsage.title': 'Which cloud providers are you currently using?',
  'cloudUsage.subtitle': 'Multiple selection possible',
  'cloudUsage.none': 'None',
  'cloudUsage.other': 'Other',
  
  // Questions
  'questions.weight': 'Weight',
  'questions.importance.low': 'low',
  'questions.importance.medium': 'medium',
  'questions.importance.high': 'very important',
  'questions.info': 'More info',
  'questions.howImportant': 'How important is this criterion to you?',
  
  // Results
  'results.title': 'Your Results',
  'results.subtitle': 'Based on your answers',
  'results.ranking': 'Ranking',
  'results.score': 'Overall Score',
  'results.export': 'Export PDF',
  'results.restart': 'Start Over',
  'results.topProvider': 'Top Recommendation',
  'results.showDetails': 'Show Details',
  'results.hideDetails': 'Hide Details',
  
  // Views
  'view.bars': 'Bar Chart',
  'view.radar': 'Radar Chart',
  'view.heatmap': 'Heatmap',
  'view.switch': 'Switch View',
  
  // Categories
  'category.Technical': 'Technical',
  'category.Regulatory': 'Regulatory',
  'category.Business': 'Business',
  
  // Weights
  'weight.adjust': 'Adjust Weight',
  'weight.reset': 'Reset',
  'weight.category': 'Category Weight',
  
  // Common
  'common.loading': 'Loading...',
  'common.error': 'Error',
  'common.close': 'Close',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.confirm': 'Confirm',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.ok': 'OK',
  
  // Errors
  'error.loadData': 'Error loading data',
  'error.noProviders': 'No providers available',
  'error.noMetrics': 'No metrics available',
  'error.exportPdf': 'Error exporting PDF',
  
  // Language
  'language.switch': 'Switch language',
  'language.switchTo.en': 'Switch to English',
  'language.switchTo.de': 'Zu Deutsch wechseln',

  // Email Modal
  'modal.title': 'Receive Report via Email',
  'modal.reportNameLabel': 'Report Name (optional)',
  'modal.reportNamePlaceholder': 'e.g. Cloud Assessment Q2 2026',
  'modal.emailLabel': 'Email Address',
  'modal.emailPlaceholder': 'your@email.com',
  'modal.emailError': 'Please enter a valid email address',
  'modal.consent': 'I agree that my email address will be stored for sending the report and potential follow-up contact. I can withdraw my consent at any time.',
  'modal.privacyLink': 'Privacy Policy',
  'modal.privacyNotice': 'Your email address will only be used for sending the report and potential follow-up contact by the operator (legal basis: Art. 6(1)(a) GDPR). Data will be automatically deleted after 180 days. You can withdraw your consent at any time by emailing david@schneider4me.de.',
  'modal.submit': 'Send Report',
  'modal.loading': 'Generating and sending report...',
  'modal.success': 'Your report has been sent successfully!',
  'modal.successDetail': 'The report was sent to {email}.',
  'modal.error': 'An error occurred while sending.',
  'modal.retry': 'Try Again',
  'modal.close': 'Close',

  // Privacy Policy
  'privacy.title': 'Privacy Policy',
  'privacy.back': 'Back to Home',

  // Cloud Act
  'cloudAct.title': 'Is it relevant to you that your cloud provider is not subject to the US Cloud Act?',
  'cloudAct.yes': 'Yes, this is important to me',
  'cloudAct.no': 'No, this is not relevant',
  'cloudAct.proceed': 'Continue',

  // Sidebar
  'sidebar.questionnaire': 'Start Questionnaire',
  'sidebar.reset': 'Reset',
  'sidebar.exportReport': 'Export Report',
  'sidebar.regulatory': 'Regulatory Overview',
  'sidebar.close': 'Close menu',
  'sidebar.openMenu': 'Open menu',
  'sidebar.closeMenu': 'Close menu',
  'sidebar.backToModeSelector': 'Back to Mode Selection',
  'sidebar.sealFramework': 'SEAL Framework',
  'sidebar.switchToCloud': 'Cloud Comparison',
  'sidebar.switchToWorkplace': 'Workplace Comparison',

  // Comparison Mode Selector
  'modeSelector.title': 'Choose Your Comparison Mode',
  'modeSelector.cloud.title': 'Cloud Solutions',
  'modeSelector.cloud.description': 'Compare cloud providers for sovereignty, compliance, and technical suitability.',
  'modeSelector.cloud.cta': 'Start Cloud Comparison',
  'modeSelector.workplace.title': 'Workplace Solutions',
  'modeSelector.workplace.description': 'Evaluate the digital sovereignty of your workplace solutions using the SEAL framework.',
  'modeSelector.workplace.cta': 'Start Workplace Comparison',

  // Workplace Provider Selection
  'workplace.providerSelection.title': 'Select Providers',
  'workplace.providerSelection.hint': 'Select at least 2 providers',
  'workplace.providerSelection.selectAll': 'Select All',
  'workplace.providerSelection.deselectAll': 'Deselect All',
  'workplace.providerSelection.continue': 'Continue',
  'workplace.providerSelection.selected': '{count} selected',

  // Provider Groups
  'workplace.group.productivityOffice': 'Productivity & Office',
  'workplace.group.communicationCollaboration': 'Communication & Collaboration',
  'workplace.group.knowledgeManagement': 'Knowledge Management',
  'workplace.group.identityAccess': 'Identity & Access',
  'workplace.group.operatingSystems': 'Operating Systems',
  'workplace.group.cloudInfrastructure': 'Cloud & Infrastructure',
  'workplace.group.endpointDeviceManagement': 'Endpoint & Device Management',
  'workplace.group.securityCompliance': 'Security & Compliance',
  'workplace.group.monitoringObservability': 'Monitoring & Observability',

  // Workplace Questionnaire
  'workplace.questionnaire.title': 'Feature Group Assessment',
  'workplace.questionnaire.rateRelevance': 'How relevant is this Feature Group?',
  'workplace.questionnaire.notRelevant': 'Not relevant',
  'workplace.questionnaire.lowRelevance': 'Low relevance',
  'workplace.questionnaire.relevant': 'Relevant',
  'workplace.questionnaire.highRelevance': 'Very relevant',
  'workplace.questionnaire.features': 'Features in this group',
  'workplace.questionnaire.continue': 'Continue to Solution Builder',

  // Solution Builder
  'workplace.solutionBuilder.title': 'Solution Builder',
  'workplace.solutionBuilder.sealScore': 'SEAL Score',
  'workplace.solutionBuilder.unassigned': 'Unassigned',
  'workplace.solutionBuilder.selectProvider': 'Select Provider',
  'workplace.solutionBuilder.solutionScore': 'Overall SEAL Score',
  'workplace.solutionBuilder.incomplete': 'Score incomplete',
  'workplace.solutionBuilder.continue': 'View Results',
  'workplace.solutionBuilder.showFeatures': 'Show Features',
  'workplace.solutionBuilder.hideFeatures': 'Hide Features',

  // Feature Ratings
  'workplace.rating.included': 'Included',
  'workplace.rating.addon': 'Addon Required',
  'workplace.rating.notIncluded': 'Not Included',

  // SEAL
  'workplace.seal.level': 'SEAL Level {level}',
  'workplace.seal.framework': 'SEAL Framework Reference',
  'workplace.seal.justification': 'Justification',

  // Workplace Results
  'workplace.results.title': 'Your Workplace Solution',
  'workplace.results.sealLevel': 'SEAL Level',
  'workplace.results.breakdown': 'Breakdown',
  'workplace.results.generateReport': 'Generate Report',
  'workplace.results.reset': 'Reset',
  'workplace.results.backToBuilder': 'Back to Solution Builder',

  // Workplace Steps
  'workplace.steps.providerSelection': 'Providers',
  'workplace.steps.questionnaire': 'Assessment',
  'workplace.steps.solutionBuilder': 'Solution Builder',
  'workplace.steps.results': 'Results',

  // Saved State
  'workplace.savedState.continue': 'Continue where you left off',
  'workplace.savedState.startNew': 'Start new',

  // Workplace SOV Flow - Step labels
  'workplace.steps.infrastructure': 'Infrastructure',
  'workplace.steps.istZustand': 'Current State',
  'workplace.steps.istScore': 'Current Score',
  'workplace.steps.sovWeights': 'Weighting',

  // Step 1: Infrastructure
  'workplace.infrastructure.title': 'How do you operate your workplace software?',
  'workplace.infrastructure.onPremise': 'All On-Premise / Self-hosted',
  'workplace.infrastructure.cloud': 'All with a Cloud Provider',
  'workplace.infrastructure.saas': 'All SaaS (Provider-hosted)',
  'workplace.infrastructure.hybrid': 'Hybrid / Mixed (different models per tool)',
  'workplace.infrastructure.cloudFollowUp': 'Which cloud provider do you use?',
  'workplace.infrastructure.continue': 'Continue',

  // Step 2: Ist-Zustand
  'workplace.istZustand.title': 'Capture Current State',
  'workplace.istZustand.description': 'Specify which tool you currently use for each Feature Group.',
  'workplace.istZustand.selectProvider': 'Select provider',
  'workplace.istZustand.selectDeployment': 'Select deployment model',
  'workplace.istZustand.unassigned': 'Unassigned',
  'workplace.istZustand.progress': '{count} of {total} assigned',
  'workplace.istZustand.continue': 'Continue',
  'workplace.istZustand.back': 'Back',

  // Step 3: Ist-Score
  'workplace.istScore.title': 'Your Current Sovereignty Score',
  'workplace.istScore.breakdown': 'Breakdown by Feature Group',
  'workplace.istScore.objectives': 'SOV Objectives Overview',
  'workplace.istScore.noScore': 'No score available',
  'workplace.istScore.sealLevel': 'SEAL Level',
  'workplace.istScore.continue': 'Continue',
  'workplace.istScore.back': 'Back',

  // Step 4: SOV Weights
  'workplace.sovWeights.title': 'SOV Objectives Weighting',
  'workplace.sovWeights.description': 'Adjust the weighting of the 8 SOV objectives to match your priorities.',
  'workplace.sovWeights.resetDefaults': 'Reset to defaults',
  'workplace.sovWeights.total': 'Total',
  'workplace.sovWeights.remaining': 'Remaining: {value}%',
  'workplace.sovWeights.skip': 'Continue with default weighting',
  'workplace.sovWeights.validationError': 'The sum of all weights must equal 100%.',
  'workplace.sovWeights.continue': 'Continue',
  'workplace.sovWeights.back': 'Back',

  // Step 5: Solution Builder (new keys)
  'workplace.solutionBuilder.description': 'Build your sovereign target configuration. Providers are sorted by SOV score.',
  'workplace.solutionBuilder.zielScore': 'Target Score',
  'workplace.solutionBuilder.istScore': 'Current Score',
  'workplace.solutionBuilder.sovScore': 'SOV Score',
  'workplace.solutionBuilder.deploymentModel': 'Deployment',
  'workplace.solutionBuilder.currentIst': 'Current',
  'workplace.solutionBuilder.back': 'Back',

  // Step 6: Results (new keys)
  'workplace.results.istScore': 'Current Score',
  'workplace.results.zielScore': 'Target Score',
  'workplace.results.delta': 'Change',
  'workplace.results.comparison': 'Before / After Comparison',
  'workplace.results.perObjective': 'SOV Objectives Comparison',
  'workplace.results.perFeatureGroup': 'Feature Group Comparison',

  // Deployment labels
  'workplace.deployment.saas': 'SaaS (Provider-hosted)',
  'workplace.deployment.managedEu': 'Managed EU (EU-Hoster)',
  'workplace.deployment.hostedEu': 'Hosted: EU Provider',
  'workplace.deployment.hostedNonEu': 'Hosted: Non-EU Provider',
  'workplace.deployment.selfHosted': 'Self-hosted (On-Premise)',

  // SOV Objective names
  'workplace.sov.sov1': 'Strategic Sovereignty',
  'workplace.sov.sov2': 'Legal & Jurisdictional Sovereignty',
  'workplace.sov.sov3': 'Data & AI Sovereignty',
  'workplace.sov.sov4': 'Operational Sovereignty',
  'workplace.sov.sov5': 'Supply Chain Sovereignty',
  'workplace.sov.sov6': 'Technological Sovereignty',
  'workplace.sov.sov7': 'Security & Compliance Sovereignty',
  'workplace.sov.sov8': 'Environmental Sustainability',

  // Other Provider
  'workplace.other.label': 'Other Provider',
  'workplace.other.selectCategory': 'Select category',
  'workplace.other.openSource': 'Open Source',
  'workplace.other.euProvider': 'EU Provider',
  'workplace.other.nonEuProvider': 'Non-EU Provider',
  'workplace.other.euOpenSource': 'EU Open Source',
  'workplace.other.nonEuOpenSource': 'Non-EU Open Source',
  'workplace.other.euProviderProprietary': 'EU Provider (Proprietary)',
  'workplace.other.nonEuProviderProprietary': 'Non-EU Provider (Proprietary)',
  'workplace.other.divider': '── Other ──',

  // Saved state (SOV flow)
  'workplace.sovState.continue': 'Continue',
  'workplace.sovState.startNew': 'Start new',
  'workplace.sovState.prompt': 'You have saved progress. Would you like to continue?',

  // Sovereignty Scoring (Cloud Comparison)
  'sovereignty.score': 'Sovereignty Score',
  'sovereignty.businessScore': 'Business Score',
  'sovereignty.domainScores': 'Domain Scores',
  'sovereignty.sealLevel': 'SEAL Level',
  'sovereignty.details': 'Sovereignty Scoring Details',
  'sovereignty.recommendation': 'Recommendation',
  'sovereignty.recommendationText': 'Based on your weightings we recommend: ',
  'sovereignty.scoreOf': 'with a score of ',
  'sovereignty.selectProfile': 'Select Profile',
  'sovereignty.cloudActDisqualified': 'Cloud Act disqualified',
  'sovereignty.c5Prerequisite': 'C5 Prerequisite',
  'sovereignty.outsideBsiScope': 'Outside BSI Scope',
  'sovereignty.downloadC3A': 'Download C3A Framework',
  'sovereignty.radarHint': 'Radar shows the 8 SOV domains (SOV-1 through SOV-8) as axes with the top 3 providers.',
  'sovereignty.heatmapHint': 'Scores per metric (0–10) grouped by SOV domain. Deeper color = higher score.',
  'sovereignty.metric': 'Metric',
  'sovereignty.avgDomainScore': 'Ø Domain Score',
  'sovereignty.businessGroup': 'Business',
  'sovereignty.frameworkMode': 'Assessment Mode',
  'sovereignty.hardRequirementFailed': 'Minimum requirement not met',
  'sovereignty.securityFilterFailed': 'Security minimum requirement not met (SOV-7 < 7)',
  'sovereignty.excluded': 'Excluded',
};
